import { useState, useEffect } from 'react'
import { useActionData, useFetcher } from 'react-router'
import {
  Button,
  CreditCardSelect,
  Dialog,
  Divider,
  Stack,
  SubmitButton,
  Typography,
  useGlobalState,
  type SubmitButtonProps,
} from '@trinity/components'
import { useHostedFields } from 'utils'

interface SelectCardDialogProps {
  children?: React.ReactNode
  isOpen: boolean
  close: () => void
  creditCards: TrinityAPI.CreditCardType[]
  clientToken: string
  ButtonProps?: Omit<SubmitButtonProps, 'isLoading' | 'type'> & { buttonText?: string }
}

export function SelectCardDialog<ActionDataType extends { message: string; error: boolean }>({
  children = null,
  isOpen,
  close,
  creditCards,
  clientToken,
  ButtonProps = {},
}: SelectCardDialogProps) {
  const actionData = useActionData() as ActionDataType
  const { setToastInfo } = useGlobalState()
  const fetcher = useFetcher<ActionDataType>()
  const [showAddCard, setShowAddCard] = useState(false)
  const [firstCard] = useState(creditCards[0])
  const { sx, buttonText, ...buttonProps } = ButtonProps

  useEffect(() => {
    if (actionData) {
      setShowAddCard(false) /* oxlint-disable-line react/set-state-in-effect */
    }
  }, [actionData])

  useEffect(() => {
    if (fetcher.data) {
      const message = fetcher.data.message
      const severity = fetcher.data.error ? 'error' : 'success'
      close()
      setToastInfo({ show: true, message, severity })
    }
  }, [close, fetcher.data, setToastInfo])

  return (
    <Dialog open={isOpen} onClose={close} PaperProps={{ sx: { width: 1 } }}>
      <Stack component={fetcher.Form} method='POST' p={4}>
        <Typography variant='h5' textAlign='center' sx={{ mb: 2 }}>
          Select Payment Card
        </Typography>
        {actionData?.message && (
          <Typography
            variant='smallBody1'
            textAlign='center'
            color={actionData.error ? 'error' : 'success.main'}
            sx={{ mb: 1 }}
          >
            {actionData.message}
          </Typography>
        )}
        <CreditCardSelect name='paymentMethodToken' creditCards={creditCards} defaultValue={firstCard?.token} />
        {!showAddCard && (
          <Typography
            variant='smallLink'
            onClick={() => setShowAddCard(true)}
            sx={{ textAlign: 'center', mt: 1, mb: showAddCard ? 4 : 8 }}
          >
            Add New Payment Method
          </Typography>
        )}
        {showAddCard && <AddCardForm clientToken={clientToken} close={() => setShowAddCard(false)} />}
        {children}
        <SubmitButton
          type='submit'
          isLoading={fetcher.state !== 'idle'}
          sx={{ width: { tablet: 1, laptop: 2 / 3 }, alignSelf: 'center', mt: 4, ...sx }}
          {...buttonProps}
        >
          {buttonText ?? 'Submit'}
        </SubmitButton>
      </Stack>
    </Dialog>
  )
}

function AddCardForm({ close, clientToken }: Pick<SelectCardDialogProps, 'close' | 'clientToken'>) {
  const { CreditCardNumber, CardholderName, ExpirationMonth, ExpirationYear, CVV, ZipCode, SubmitButton } =
    useHostedFields({ clientToken })

  return (
    <Stack spacing={4} sx={{ pb: 4 }}>
      <Divider />
      <Stack direction={{ md: 'row' }} spacing={{ xs: 4, md: 2 }}>
        <Stack spacing={1}>
          <Typography variant='body3'>Cardholder Name</Typography>
          <CardholderName />
        </Stack>
        <Stack spacing={1}>
          <Typography variant='body3'>Card Number</Typography>
          <CreditCardNumber />
        </Stack>
      </Stack>
      <Stack direction={{ md: 'row' }} spacing={{ xs: 4, md: 2 }}>
        <Stack direction='row' spacing={2}>
          <Stack spacing={1}>
            <Typography variant='body3'>Exp. Month</Typography>
            <ExpirationMonth />
          </Stack>
          <Stack spacing={1}>
            <Typography variant='body3'>Exp. Year</Typography>
            <ExpirationYear />
          </Stack>
        </Stack>
        <Stack direction='row' spacing={2}>
          <Stack spacing={1}>
            <Typography variant='body3'>CVC Number</Typography>
            <CVV />
          </Stack>
          <Stack spacing={1}>
            <Typography variant='body3'>Zip Code</Typography>
            <ZipCode />
          </Stack>
        </Stack>
      </Stack>
      <Stack direction='row' spacing={2} justifyContent={{ xs: 'center', md: 'flex-end' }}>
        <SubmitButton size='small' />
        <Button size='small' variant='outlined' onClick={close}>
          Cancel
        </Button>
      </Stack>
      <Divider />
    </Stack>
  )
}
